// hls.js is loaded dynamically inside TrailerPlayer (see tryUrl) so its
// ~500KB isn't bundled into the homepage load for everyone who never
// even sees a trailer start playing.
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SmartImage from '../ui/SmartImage';
import { useAuth } from '../../context/AuthContext';

// Branded domain — preferred for images. Plain <img> tags don't trigger a
// CORS check the way fetch/XHR/HLS.js does, so the CORS restriction found
// on this domain for trailers doesn't apply to regular image loading.
const CDN_BRANDED = 'https://cdn.shortflixindia.com/';
// Open CloudFront CDN — kept only as an automatic per-suffix fallback.
const CDN_OPEN = 'https://d2q1oltu9dnqie.cloudfront.net/';

function corsFixUrl(url) {
  if (!url) return url;
  // Signed URLs (/t/{token}/) must NEVER be rewritten — token breaks.
  if (url.includes('/t/')) return url;
  // Only the dead legacy CloudFront ID needs normalizing now — both the
  // branded domain and the open CDN are valid destinations for images.
  return url.replace('https://d12it2ctp7su6p.cloudfront.net/', CDN_BRANDED);
}

function buildTrailerUrls(movie) {
  const TRAILER_CDN = 'https://cdn.shortflixindia.com/';
  const urls = [];

  // Trailers must always resolve to cdn.shortflixindia.com — that's the
  // domain with correct CORS configured for the production origin.
  // The open CDN (d2q1oltu9dnqie) does NOT have working CORS for trailers.
  // Signed video URLs (/t/{token}/) are never trailers — leave untouched.
  const fixTrailerUrl = (u) => {
    if (!u || u.includes('/t/')) return u;
    return u
      .replace('https://d2q1oltu9dnqie.cloudfront.net/', TRAILER_CDN)
      .replace('https://d12it2ctp7su6p.cloudfront.net/', TRAILER_CDN);
  };

  // 1. API-provided trailerUrl — try first, but DON'T early-return:
  //    keep constructed fallbacks in case this URL 404s
  const apiUrl = movie?.trailerUrl || movie?.trailerurl;
  if (apiUrl) urls.push(fixTrailerUrl(apiUrl));

  // 2. show_trailer === 0 means no trailer
  if (!apiUrl && movie?.show_trailer === 0) return [];

  // 3. Constructed fallbacks — try filmname (slug) and filmid variants
  const slug   = movie?.filmname || movie?.programname;
  const filmid = movie?.filmid || movie?.id || movie?.programid;

  if (slug)                            urls.push(`${TRAILER_CDN}${slug}-trailer/playlist.m3u8`);
  if (filmid && filmid !== slug)       urls.push(`${TRAILER_CDN}${filmid}-trailer/playlist.m3u8`);

  // De-duplicate while preserving order
  return [...new Set(urls)];
}

// Format duration: "136m" → "2h 08m", "47m" → "47m", "2:16:08" → "2h 16m"
function formatDuration(raw) {
  if (!raw) return null;
  const str = String(raw).trim();
  // Already in "Xh Ym" format
  if (/^\d+h/.test(str)) return str;
  // "HH:MM:SS" or "H:MM:SS"
  const hms = str.match(/^(\d+):(\d{2}):\d{2}$/);
  if (hms) {
    const h = parseInt(hms[1], 10);
    const m = parseInt(hms[2], 10);
    if (h > 0) return `${h}h ${m.toString().padStart(2,'0')}m`;
    return `${m}m`;
  }
  // "136m" or "47m" or bare number (minutes)
  const mins = str.match(/^(\d+)\s*m?$/i);
  if (mins) {
    const total = parseInt(mins[1], 10);
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (h > 0) return `${h}h ${m.toString().padStart(2,'0')}m`;
    return `${m}m`;
  }
  return str; // fallback: show as-is
}

function getBannerUrls(movie, isMobile = false) {
  const filmid = movie?.filmid || movie?.id || movie?.programid;
  const urls = [];

  const pick = (field) => {
    const url = Array.isArray(field) ? field[0] : field;
    return (url && typeof url === 'string' && url.startsWith('http')) ? corsFixUrl(url) : null;
  };

  // ── Mobile: build the device-specific banner URL directly ──────────────────
  // Confirmed naming convention from the CDN: {filmid}-mobile-banner.webp/.png
  // (e.g. superdogcat-mobile-banner.webp). We build this FIRST and trust it
  // over the API's own banner[] field — in practice the API's banner[] field
  // has been observed returning the wide TV banner even when called from the
  // mobile-oriented endpoint, so blindly trusting it sends phones a 1.6MB+
  // widescreen image instead of the ~75KB mobile-cropped one.
  if (isMobile && filmid) {
    urls.push(`${CDN_BRANDED}${filmid}/${filmid}-mobile-banner.webp`);
    urls.push(`${CDN_OPEN}${filmid}/${filmid}-mobile-banner.webp`);
    urls.push(`${CDN_BRANDED}${filmid}/${filmid}-mobile-banner.png`);
    urls.push(`${CDN_OPEN}${filmid}/${filmid}-mobile-banner.png`);
  }

  // Only accept the API's banner[] field on mobile if the entry is itself
  // explicitly a mobile-named asset — never trust it unconditionally, since
  // it can silently be the TV banner instead.
  if (isMobile && movie?.banner) {
    const arr = Array.isArray(movie.banner) ? movie.banner : [movie.banner];
    for (const entry of arr) {
      if (typeof entry === 'string' && entry.startsWith('http') && entry.includes('mobile')) {
        urls.push(corsFixUrl(entry));
      }
    }
  }

  // 1. TV banner API fields — wide cinematic crop, best for hero
  for (const field of [movie?.tvbanner, movie?.banner_tv, movie?.bannertv, movie?.bannertext]) {
    const url = pick(field);
    if (url) urls.push(url);
  }

  // 2. CDN TV banner patterns
  if (filmid) {
    urls.push(`${CDN_BRANDED}${filmid}/${filmid}-tv-banner.webp`);
    urls.push(`${CDN_OPEN}${filmid}/${filmid}-tv-banner.webp`);
    urls.push(`${CDN_BRANDED}${filmid}/${filmid}-tv-banner.png`);
    urls.push(`${CDN_OPEN}${filmid}/${filmid}-tv-banner.png`);
  }

  // 3. bannerImage API field (used by detail page)
  for (const field of [movie?.bannerImage, movie?.bannerimage]) {
    const url = pick(field);
    if (url) urls.push(url);
  }

  // 4. banner array — skip mobile crops here since mobile already has its
  //    own dedicated entries above; desktop/tablet still skip them, since
  //    a "-mobile-" crop is meant for phones, not a wide desktop hero.
  if (movie?.banner) {
    const arr = Array.isArray(movie.banner) ? movie.banner : [movie.banner];
    for (const entry of arr) {
      if (typeof entry === 'string' && entry.startsWith('http') && !entry.includes('mobile')) {
        urls.push(corsFixUrl(entry));
      }
    }
  }

  // 5. Web/wide banner fallback — same as detail page
  if (filmid) {
    urls.push(`${CDN_BRANDED}${filmid}/${filmid}-banner.webp`);
    urls.push(`${CDN_OPEN}${filmid}/${filmid}-banner.webp`);
    urls.push(`${CDN_BRANDED}${filmid}/${filmid}-banner.png`);
    urls.push(`${CDN_OPEN}${filmid}/${filmid}-banner.png`);
    urls.push(`${CDN_BRANDED}${filmid}/${filmid}-L-1.png`);
    urls.push(`${CDN_OPEN}${filmid}/${filmid}-L-1.png`);
  }

  // Deduplicate
  return [...new Set(urls)];
}

// Preload banner image for the next slide so there's no flicker
function preloadBanner(movie, isMobile) {
  const url = getBannerUrls(movie, isMobile)[0];
  if (url) { const img = new Image(); img.src = url; }
}

// ── Thumbnail strip ───────────────────────────────────────────────────────────
function StripThumb({ movie, active, onClick, index }) {
  return (
    <button className={`banner-strip-item ${active ? 'active' : ''}`} onClick={onClick}>
      <div className="strip-thumb-wrap">
        <SmartImage movie={movie} type="landscape" alt={movie?.title} className="strip-thumb-img" />
        <div className="strip-thumb-overlay" />
        {active && <div className="strip-active-bar" />}
        <span className="strip-index">{String(index + 1).padStart(2, '0')}</span>
      </div>
      <p className="strip-title">{movie?.title}</p>
    </button>
  );
}

// ── HLS Trailer Player ────────────────────────────────────────────────────────
// THE FIX: urls array is memoized in parent so it never gets a new reference
// unless the actual slide/movie changes. This means useEffect([urls]) below
// only fires when the slide changes — never when muted state changes.
// Muted is handled by a SEPARATE useEffect([muted]) that only writes video.muted.
function TrailerPlayer({ urls, muted, onFail, onEnded }) {
  const videoRef = useRef(null);
  const hlsRef   = useRef(null);
  const mutedRef = useRef(muted);
  const cbRefs   = useRef({ onFail, onEnded });

  useEffect(() => { cbRefs.current = { onFail, onEnded }; });

  // ── MUTE FIX: only touches video.muted — never restarts HLS ──────────────
  useEffect(() => {
    mutedRef.current = muted;
    const video = videoRef.current;
    if (!video) return;
    // Direct DOM property write. Browsers allow this on a playing video.
    // This DOES NOT trigger any HLS event, does NOT pause/restart the stream.
    video.muted = muted;
  }, [muted]);

  // ── HLS setup: only re-runs when urls changes (i.e. slide changes) ────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !urls?.length) return;
    let cancelled = false;
    let idx = 0;

    function destroyHls() {
      if (hlsRef.current) {
        try { hlsRef.current.destroy(); } catch (_) {}
        hlsRef.current = null;
      }
    }

    function tryNext() {
      if (cancelled) return;
      if (idx >= urls.length) { cbRefs.current.onFail?.(); return; }
      tryUrl(urls[idx++]);
    }

        function attachHls(Hls, src) {
      const hls = new Hls({
        startLevel: -1,
        enableWorker: true,
        maxBufferLength: 20,
        xhrSetup: xhr => { xhr.withCredentials = false; },
      });
      hlsRef.current = hls;
      hls.on(Hls.Events.ERROR, (_, d) => { if (d.fatal && !cancelled) tryNext(); });
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (cancelled) return;
        video.muted = true;
        video.loop  = false;
        video.play()
          .then(() => { if (!cancelled) video.muted = mutedRef.current; })
          .catch(() => { if (!cancelled) cbRefs.current.onFail?.(); });
      });
      hls.loadSource(src);
      hls.attachMedia(video);
    }

    async function tryUrl(src) {
      destroyHls();
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.loop  = false;
        video.src   = src;
        video.muted = true;
        video.play()
          .then(() => { if (!cancelled) video.muted = mutedRef.current; })
          .catch(() => { if (!cancelled) tryNext(); });
        return;
      }
      if (cancelled) return;
      const { default: Hls } = await import('hls.js');
      if (cancelled) return;
      if (Hls.isSupported()) {
        attachHls(Hls, src);
      } else {
        tryNext();
      }
    }

    const onEnd = () => { if (!cancelled) cbRefs.current.onEnded?.(); };
    video.addEventListener('ended', onEnd);
    tryNext();

    return () => {
      cancelled = true;
      video.removeEventListener('ended', onEnd);
      destroyHls();
      try { video.pause(); video.removeAttribute('src'); video.load(); } catch (_) {}
    };
  // CRITICAL: urls must be a stable reference (memoized in parent)
  // so this effect only fires on real slide changes, not on muted toggle
  }, [urls]); // eslint-disable-line

  return <video ref={videoRef} className="hero-yt-frame" playsInline muted crossOrigin="anonymous" />;
}

// ── Main HeroBanner ───────────────────────────────────────────────────────────
const IMAGE_TO_TRAILER_DELAY = 2000;
// Auto-advance image-only slides after this many ms (when no trailer exists)
const IMAGE_ONLY_DISPLAY_MS  = 6000;

export default function HeroBanner({ movies = [] }) {
  const items = movies.slice(0, 5);
  const [current,       setCurrent]    = useState(0);
  const [showTrailer,   setShowTrailer] = useState(false);
  const [trailerFailed, setFailed]     = useState(false);
  const [muted,         setMuted]      = useState(true);
  const [fade,          setFade]       = useState(true);
  // Disable trailer on mobile — banner image only for performance and UX
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const delayRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated, isPremium, signIn } = useAuth();

  const movie  = items[current];
  const filmId = movie?.filmid || movie?.id || movie?.programid;

  // Same subscription gate as ShowDetailPage's "Subscribe to Watch" button —
  // the banner's Watch Now was skipping this entirely and playing the full
  // movie for anyone, subscribed or not.
  const handleWatchNow = () => {
    if (!isAuthenticated) {
      sessionStorage.setItem('sf_post_login_redirect', `/show/${filmId}`);
      signIn();
      return;
    }
    if (!isPremium) {
      sessionStorage.setItem('sf_post_subscribe_redirect', `/show/${filmId}`);
      navigate('/subscription');
      return;
    }
    navigate(`/watch/${filmId}/main`);
  };
  const bgUrls = getBannerUrls(movie, isMobile);
  const [bgIdx, setBgIdx] = useState(0);
  const bgUrl  = bgUrls[bgIdx] || null;

  // ── THE CRITICAL FIX ──────────────────────────────────────────────────────
  // Memoize trailerUrls so the array reference is STABLE across renders.
  // Without this: every render of HeroBanner (including mute toggle) creates
  // a new array → TrailerPlayer's useEffect([urls]) sees a "changed" dependency
  // → HLS is destroyed and restarted → trailer plays from beginning.
  // With useMemo: array reference only changes when current slide changes.
  // Mobile: trailerUrls is always [] — no trailer ever plays on mobile.
  // Desktop: memoized so TrailerPlayer only re-mounts when the slide changes.
  const trailerUrls = useMemo(
    () => isMobile ? [] : buildTrailerUrls(movie),
    [current] // eslint-disable-line
  );
  const hasTrailer = trailerUrls.length > 0 && !trailerFailed;

  useEffect(() => {
    setBgIdx(0);
    setShowTrailer(false);
    setFailed(false);
    clearTimeout(delayRef.current);
    const nextIdx = (current + 1) % items.length;
    if (items[nextIdx]) preloadBanner(items[nextIdx], isMobile);

    if (!isMobile && trailerUrls.length > 0) {
      // Desktop only: show banner image briefly then crossfade to trailer
      delayRef.current = setTimeout(() => setShowTrailer(true), IMAGE_TO_TRAILER_DELAY);
    } else {
      // Mobile (always) or desktop with no trailer: auto-advance on a timer
      delayRef.current = setTimeout(() => {
        setFade(false);
        setTimeout(() => { setCurrent(c => (c + 1) % items.length); setFade(true); }, 350);
      }, IMAGE_ONLY_DISPLAY_MS);
    }
    return () => clearTimeout(delayRef.current);
  }, [current]); // eslint-disable-line

  const handleFail = useCallback(() => {
    setFailed(true);
    setShowTrailer(false);
    // Trailer failed — treat as image-only, auto-advance after display time
    clearTimeout(delayRef.current);
    delayRef.current = setTimeout(() => {
      setFade(false);
      setTimeout(() => { setCurrent(c => (c + 1) % items.length); setFade(true); }, 350);
    }, IMAGE_ONLY_DISPLAY_MS);
  }, [items.length]); // eslint-disable-line

  const goNext = useCallback(() => {
    setFade(false);
    setTimeout(() => { setCurrent(c => (c + 1) % items.length); setFade(true); }, 350);
  }, [items.length]);

  const goTo = useCallback((i) => {
    if (i === current) return;
    clearTimeout(delayRef.current);
    setFade(false);
    setTimeout(() => { setCurrent(i); setFade(true); }, 350);
  }, [current]);

  if (!items.length) return <div className="hero-empty" />;

  const rawGenre  = movie?.genres || movie?.genre || '';
  const genreLabel = rawGenre && !['banner','BANNER','Banner'].includes(rawGenre.trim()) ? rawGenre.trim() : null;

  // ── Mobile: dedicated layout matching the Android app ──────────────────────
  // The app shows the mobile-banner artwork at its natural tall aspect ratio
  // with the title already baked into the image, then a plain meta line and
  // action buttons below — no dark gradient or duplicate title text overlaid
  // on top of the image. Trailers never play on mobile (trailerUrls is always
  // [] here), so this only ever needs to handle the static image case.
  if (isMobile) {
    return (
      <section className="hero-mobile">
        <div className="hero-mobile-poster">
          {bgUrl && (
            <img
              key={bgUrl}
              src={bgUrl}
              alt={movie?.title || ''}
              fetchpriority={current === 0 ? 'high' : 'auto'}
              loading={current === 0 ? 'eager' : 'lazy'}
              onError={() => setBgIdx(i => Math.min(i + 1, bgUrls.length - 1))}
              className="hero-mobile-poster-img"
            />
          )}
          <div className="hero-mobile-fade" />
        </div>

        <div className="hero-mobile-below">
          <div className="hero-mobile-meta">
            {movie?.durationtext && <span>{formatDuration(movie.durationtext)}</span>}
            {movie?.releaseyear  && <span>{movie.releaseyear}</span>}
            {genreLabel           && <span>{genreLabel}</span>}
            {movie?.rating        && <span className="hero-v2-rating">{movie.rating}</span>}
          </div>
          <div className="hero-mobile-actions">
            <button className="btn-hv2-primary" onClick={handleWatchNow}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              Watch Now
            </button>
            <button className="btn-hv2-info" onClick={() => navigate(`/show/${filmId}`)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              More Info
            </button>
          </div>
        </div>

        {items.length > 1 && (
          <div className="hero-mobile-dots">
            {items.map((m, i) => (
              <button
                key={m?.filmid || m?.id || i}
                className={`hero-mobile-dot${i === current ? ' active' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="hero-v2">
      <div className={`hero-v2-bg ${fade ? 'visible' : ''}`}>
        {bgUrl && (
          <img
            key={bgUrl}
            src={bgUrl}
            alt=""
            // The very first slide shown on initial page load is almost always
            // the LCP (Largest Contentful Paint) element for the homepage —
            // fetch it immediately at high priority instead of at the browser's
            // default priority, which can delay it behind other page resources.
            fetchpriority={current === 0 ? 'high' : 'auto'}
            loading={current === 0 ? 'eager' : 'lazy'}
            onError={() => setBgIdx(i => Math.min(i + 1, bgUrls.length - 1))}
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: 'center 20%',
              opacity: showTrailer ? 0 : 1,
              transition: 'opacity 0.8s ease',
              animation: !showTrailer ? 'heroZoom 8s ease-out forwards' : 'none',
              display: 'block',
            }}
          />
        )}
        {hasTrailer && (
          <div className="hero-yt-wrap" style={{ opacity: showTrailer ? 1 : 0, transition: 'opacity 0.8s ease' }}>
            <TrailerPlayer
              key={current}
              urls={trailerUrls}
              muted={muted}
              onFail={handleFail}
              onEnded={goNext}
            />
            <div className="hero-yt-vignette" />
          </div>
        )}
        <div className="hero-v2-gradient" />
      </div>
      <div className="hero-v2-bottom-blend" />

      <div className="hero-v2-content">
        {genreLabel && (
          <div className="hero-v2-genre">
            <span className="hero-v2-genre-dot" />{genreLabel}
          </div>
        )}
        <h1 className="hero-v2-title">{movie?.title}</h1>
        <div className="hero-v2-meta">
          {movie?.rating       && <span className="hero-v2-rating">{movie.rating}</span>}
          {movie?.releaseyear  && <span>{movie.releaseyear}</span>}
          {movie?.durationtext && <span>{formatDuration(movie.durationtext)}</span>}
          {movie?.language     && <span className="hero-v2-lang">{movie.language}</span>}
        </div>
        {(movie?.description || movie?.crisp) && (
          <p className="hero-v2-fulldesc">{movie.description || movie.crisp}</p>
        )}
        <div className="hero-v2-actions">
          <button className="btn-hv2-primary" onClick={handleWatchNow}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            Watch Now
          </button>
          <button className="btn-hv2-info" onClick={() => navigate(`/show/${filmId}`)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            More Info
          </button>
        </div>
      </div>

      <div className="banner-bottom-right">
        {showTrailer && (
          <button className="btn-hv2-mute-corner" onClick={() => setMuted(m => !m)} title={muted ? 'Unmute' : 'Mute'}>
            {muted ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
              </svg>
            )}
          </button>
        )}
        {items.length > 1 && (
          <div className="banner-strip">
            {items.map((m, i) => (
              <StripThumb key={m?.filmid || m?.id || i} movie={m} active={i === current} onClick={() => goTo(i)} index={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
